import type { ReactElement } from "react";
import { useSelector } from "react-redux";
import { Logout, Person, Settings } from "@mui/icons-material";
import {
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  ListItemIcon,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  type PopperProps,
  Typography,
} from "@mui/material";

import { useAdminDispatch } from "@/apps/admin/hooks/useAdminDispatch";

import {
  selectUser,
  signout,
} from "../../features/authentication/authenticationSlice";
export const topMenuHeight = "64px";

type ProfileMenuProps = {
  anchor: HTMLElement | null;
  open: boolean;
  onClose: () => void;
};

const ProfileMenu = ({ anchor, open, onClose }: ProfileMenuProps) => {
  const dispatch = useAdminDispatch();
  const user = useSelector(selectUser);
  return (
    <>
      <PopperMenu
        anchorEl={anchor}
        id="account-menu"
        disablePortal
        open={open}
        onClose={onClose}
        onClick={onClose}
        placement="bottom-end"
      >
        <MenuList>
          <Box px={2} width="220px">
            <Typography variant="body1">{user?.displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <MenuItem disabled onClick={onClose}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            Settings
          </MenuItem>
          <MenuItem disabled onClick={onClose}>
            <ListItemIcon>
              <Person fontSize="small" />
            </ListItemIcon>
            Person
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => dispatch(signout())}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </MenuList>
      </PopperMenu>
    </>
  );
};

export default ProfileMenu;

type PopperMenuProps = Omit<PopperProps, "children"> & {
  open: boolean;
  onClose: () => void;
  // ClickAwayListener takes exactly one element that holds a ref
  children: ReactElement;
};

const PopperMenu = ({ open, onClose, children, ...props }: PopperMenuProps) => {
  return (
    <Popper open={open} transition {...props}>
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} style={{ transformOrigin: "right top" }}>
          <Paper elevation={8}>
            <ClickAwayListener onClickAway={onClose}>
              {children}
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};
